import { logger } from "@/lib/logger";
import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { performanceMonitor } from "@/lib/performance";

interface UserProfile {
  id: string;
  user_id: string;
  role?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAdmin: boolean;
  adminLoading: boolean;
  primaryRole: string | null;
  isAuthReady: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// PHASE 4: Constants for timeout handling
const PROFILE_FETCH_TIMEOUT = 10000; // 10 seconds (increased from 5s)
const RPC_TIMEOUT = 5000; // 5 seconds for RPC calls
const MAX_RETRIES = 2;
const RETRY_DELAYS = [1000, 2000]; // Exponential backoff delays

// Helper: Fetch with timeout and retry
async function fetchWithRetry<T>(
  fetchFn: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number,
  maxRetries: number = MAX_RETRIES,
  retryDelays: number[] = RETRY_DELAYS
): Promise<T | null> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    
    try {
      const result = await fetchFn(controller.signal);
      clearTimeout(timeoutId);
      return result;
    } catch (error: any) {
      clearTimeout(timeoutId);
      lastError = error;
      
      // Don't retry on abort or if it's the last attempt
      if (error?.name === 'AbortError' || attempt >= maxRetries) {
        break;
      }
      
      // Wait before retrying
      if (attempt < maxRetries) {
        const delay = retryDelays[attempt] || retryDelays[retryDelays.length - 1];
        logger.debug(`[Auth] Retry ${attempt + 1}/${maxRetries} after ${delay}ms...`);
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }
  
  if (lastError) {
    logger.warn('[Auth] All retries exhausted:', lastError.message);
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [primaryRole, setPrimaryRole] = useState<string | null>(null);
  const [adminLoading, setAdminLoading] = useState<boolean>(false);

  // PHASE 4: Improved profile fetch with retry and longer timeout
  const fetchUserProfile = useCallback(async (userId: string, signal?: AbortSignal): Promise<UserProfile | null> => {
    try {
      const result = await fetchWithRetry(
        async (sig) => {
          const { data, error } = await supabase
            .rpc('get_profile_safe', { target_user_id: userId })
            .abortSignal(sig)
            .maybeSingle();
          
          if (error) throw error;
          return data as UserProfile | null;
        },
        PROFILE_FETCH_TIMEOUT,
        MAX_RETRIES
      );
      
      return result;
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        logger.error('[Auth] Profile fetch error:', error.message);
      }
      return null;
    }
  }, []);

  // Resolve organization_id from memberships and selection
  const ACTIVE_ORG_KEY = 'active_organization_id';
  const resolveOrganizationId = useCallback(async (userId: string): Promise<string | null> => {
    try {
      const stored = localStorage.getItem(ACTIVE_ORG_KEY);
      
      const result = await fetchWithRetry(
        async (sig) => {
          const { data, error } = await supabase
            .from('organization_members')
            .select('organization_id, created_at, organizations(type)')
            .eq('user_id', userId)
            .order('created_at', { ascending: false })
            .abortSignal(sig);
          
          if (error) throw error;
          return data;
        },
        RPC_TIMEOUT,
        1 // Only 1 retry for this
      );
      
      if (!result) return stored || null;
      
      const memberships = result as any[];
      if (!memberships.length) return null;

      // If a stored active organization exists and is valid, prefer it
      if (stored && memberships.some(m => m.organization_id === stored)) {
        return stored;
      }

      // Prefer sponsor organizations for sponsor areas
      const sponsorMem = memberships.find(m => m.organizations?.type === 'sponsor');
      if (sponsorMem) return sponsorMem.organization_id;

      // Fallback to the most recent membership
      return memberships[0].organization_id || null;
    } catch (e) {
      return localStorage.getItem(ACTIVE_ORG_KEY) || null;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      const profile = await fetchUserProfile(user.id);
      if (profile) {
        const orgId = await resolveOrganizationId(user.id);
        setUserProfile({ ...profile, organization_id: orgId });
      } else {
        setUserProfile(null);
      }
    }
  }, [user?.id, fetchUserProfile, resolveOrganizationId]);

  useEffect(() => {
    let mounted = true;
    let abortController = new AbortController();

    // Helper to check if user is suspended
    const checkAndSetProfile = async (userId: string, email?: string) => {
      const profile = await fetchUserProfile(userId, abortController.signal);
      if (!mounted) return;
      
      if (profile) {
        // Check if user is suspended
        if (profile.suspended_at) {
          logger.debug('[Auth] User is suspended, signing out');
          await supabase.auth.signOut();
          toast({
            title: "Account Suspended",
            description: "Your account has been suspended. Please contact support.",
            variant: "destructive",
          });
          setUser(null);
          setSession(null);
          setUserProfile(null);
          return false;
        }
        const orgId = await resolveOrganizationId(userId);
        setUserProfile({ ...profile, organization_id: orgId });
        return true;
      } else {
        // Fallback profile from session
        const fallbackProfile: UserProfile = {
          id: '',
          user_id: userId,
          email: email,
          role: undefined
        };
        setUserProfile(fallbackProfile);
        logger.warn('[Auth] Using fallback profile due to fetch failure');
        return true;
      }
    };

    // Listener for ONGOING auth changes - does NOT control loading state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (!mounted) return;
        
        // Only update session/user state - don't touch loading
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        // Fire and forget profile fetch for ongoing changes (not initial load)
        if (currentSession?.user) {
          // Use setTimeout(0) to defer and avoid Supabase deadlock
          setTimeout(() => {
            if (!mounted) return;
            checkAndSetProfile(currentSession.user.id, currentSession.user.email);
          }, 0);
        } else {
          setUserProfile(null);
        }
      }
    );

    // INITIAL load - controls loading state
    const initializeAuth = async () => {
      logger.debug('[lifecycle] Auth - initializing');
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (!mounted) return;

        // Check if session should be cleared based on remember me preference
        if (currentSession) {
          const rememberMe = localStorage.getItem('auth_remember_me');
          const sessionExpiry = localStorage.getItem('auth_session_expiry');
          
          if (rememberMe === 'false' && sessionExpiry) {
            const expiryTime = parseInt(sessionExpiry, 10);
            if (Date.now() > expiryTime) {
              logger.debug('[Auth] Session expired (remember me not checked), signing out');
              await supabase.auth.signOut();
              localStorage.removeItem('auth_session_expiry');
              setSession(null);
              setUser(null);
              setProfileLoaded(true);
              setLoading(false);
              return;
            }
          }
        }

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        // Fetch profile BEFORE setting loading false
        if (currentSession?.user) {
          await checkAndSetProfile(currentSession.user.id, currentSession.user.email);
        }
        
        if (mounted) {
          setProfileLoaded(true);
        }
      } catch (error) {
        logger.error("[Auth] Error initializing auth:", error);
        if (mounted) setProfileLoaded(true);
      } finally {
        if (mounted) {
          setLoading(false);
          logger.debug('[lifecycle] Auth - initialization complete');
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      abortController.abort();
      subscription.unsubscribe();
    };
  }, [fetchUserProfile, resolveOrganizationId]);

  // Realtime subscription for profile updates
  useEffect(() => {
    if (!user?.id) return;

    logger.debug("[Auth] Setting up realtime subscription for profile updates");

    const channel = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          logger.debug("[Auth] Profile updated in realtime:", payload);
          if (payload.new) {
            // MERGE instead of replace to preserve computed fields like organization_id
            setUserProfile(prev => ({
              ...prev,
              ...(payload.new as UserProfile)
            }));
          }
        }
      )
      .subscribe();

    return () => {
      logger.debug("[Auth] Unsubscribing from profile updates");
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Realtime: keep organization_id in sync with membership changes
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('org-membership-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'organization_members', filter: `user_id=eq.${user.id}` },
        async () => {
          const orgId = await resolveOrganizationId(user.id);
          if (orgId) {
            setUserProfile(prev => (prev ? { ...prev, organization_id: orgId } : prev));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, resolveOrganizationId]);

  // Broadcast listener for staff-initiated profile updates
  useEffect(() => {
    if (!user?.id) return;

    logger.debug("[Auth] Setting up broadcast listener for staff updates");

    const broadcastChannel = supabase
      .channel(`profile-updates:${user.id}`)
      .on('broadcast', { event: 'profile_updated' }, async (payload) => {
        logger.debug("[Auth] Staff updated profile:", payload);
        
        try {
          const freshProfile = await fetchUserProfile(user.id);
          
          if (freshProfile) {
            const orgId = await resolveOrganizationId(user.id);
            setUserProfile({ ...(freshProfile as UserProfile), organization_id: orgId });
            
            toast({
              title: "Profile Updated",
              description: payload.payload?.updated_by 
                ? `Your profile was updated by ${payload.payload.updated_by}`
                : "Your profile has been updated successfully",
              duration: 5000,
            });
          }
        } catch (err) {
          logger.error('[Auth] Error handling broadcast update:', err);
          toast({
            title: "Update Sync Failed",
            description: "Unable to sync your profile updates. Please refresh the page.",
            variant: "destructive",
          });
        }
      })
      .subscribe();

    return () => {
      logger.debug("[Auth] Unsubscribing from broadcast channel");
      supabase.removeChannel(broadcastChannel);
    };
  }, [user?.id, fetchUserProfile, resolveOrganizationId]);

  // PHASE 4: Improved admin/role check with better timeout handling
  // FIX: Remove userProfile?.role from dependencies to prevent race condition
  // The effect was re-running mid-execution when userProfile changed
  useEffect(() => {
    let cancelled = false;
    
    const loadRole = async () => {
      if (!user?.id) {
        setIsAdmin(false);
        setPrimaryRole(null);
        setAdminLoading(false);
        return;
      }

      performanceMonitor.startMark('role_load');

      try {
        // Verify session is still valid before making RPC calls
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession || cancelled) {
          performanceMonitor.endMark('role_load', { success: false, reason: 'no_session' });
          // Still try to set from profile if available
          setAdminLoading(false);
          return;
        }
        
        logger.debug('[Auth] Checking admin status and role for user:', user.id);
        setAdminLoading(true);
        
        performanceMonitor.startMark('admin_rpc');
        performanceMonitor.startMark('role_rpc');
        
        // PHASE 4: Run both RPCs in parallel with improved timeout handling
        const [adminResult, roleResult] = await Promise.allSettled([
          fetchWithRetry(
            async () => {
              const { data, error } = await supabase.rpc('user_has_admin_role', { check_user_id: user.id });
              if (error) throw error;
              return data;
            },
            RPC_TIMEOUT,
            1
          ),
          fetchWithRetry(
            async () => {
              const { data, error } = await supabase.rpc('get_current_user_role');
              if (error) throw error;
              return data;
            },
            RPC_TIMEOUT,
            1
          )
        ]);
        
        const adminDuration = performanceMonitor.endMark('admin_rpc', { 
          success: adminResult.status === 'fulfilled',
          userId: user.id 
        });
        
        const roleDuration = performanceMonitor.endMark('role_rpc', { 
          success: roleResult.status === 'fulfilled',
          userId: user.id 
        });
        
        performanceMonitor.endMark('role_load', { 
          success: true,
          userId: user.id,
          totalDuration: Math.max(adminDuration, roleDuration)
        });
        
        if (cancelled) return;
        
        // Process admin result - RPC is source of truth
        if (adminResult.status === 'fulfilled' && adminResult.value !== null) {
          logger.debug(`[Auth] Admin check completed in ${adminDuration.toFixed(2)}ms: ${adminResult.value}`);
          setIsAdmin(Boolean(adminResult.value));
        } else {
          // Fallback to profile role check
          logger.debug('[Auth] Admin RPC failed, will rely on profile role in SmartRedirect');
          setIsAdmin(false);
        }
        
        // Process role result - RPC is source of truth
        if (roleResult.status === 'fulfilled' && roleResult.value !== null) {
          logger.debug(`[Auth] Role loaded in ${roleDuration.toFixed(2)}ms: ${roleResult.value}`);
          setPrimaryRole(roleResult.value as string);
        } else {
          logger.debug('[Auth] Role RPC failed, will rely on profile role in SmartRedirect');
          setPrimaryRole(null);
        }
        
        if (!cancelled) {
          setAdminLoading(false);
        }
          
      } catch (e) {
        performanceMonitor.endMark('role_load', { success: false, error: String(e) });
        logger.error('[Auth] Error loading role/admin status:', e);
        if (!cancelled) {
          setIsAdmin(false);
          setPrimaryRole(null);
          setAdminLoading(false);
        }
      }
    };
    
    loadRole();
    return () => { cancelled = true; };
  }, [user?.id]); // FIX: Only depend on user.id, not userProfile

  // PHASE 2, 3 & 4: Sync authentication state to DOM for external scripts (WhatsApp widget)
  // Also includes MutationObserver for persistent hiding
  useEffect(() => {
    const isAuthenticated = !!user;
    
    // Set DOM attribute for CSS-based hiding
    document.documentElement.dataset.authenticated = isAuthenticated ? 'true' : 'false';
    
    // Set global flag for JavaScript-based checks (loadChat function in index.html)
    (window as any).__PTC_USER_AUTHENTICATED = isAuthenticated;
    
    logger.debug('[Auth] Set data-authenticated:', isAuthenticated);
    
    // PHASE 3: Extended selectors including chat-widget custom element
    const widgetSelectors = [
      '.lc-live-chat-button',
      '.lc-chat-container',
      '[data-widget-id="69285244d47c137447a1fa64"]',
      'iframe[src*="leadconnectorhq"]',
      '.lc-widget-open-icon',
      '.lc-chat-widget-container',
      '.lc_text-widget',
      '.lc_text-widget-container',
      'chat-widget', // Custom element
      '[class*="lc-"]', // Any class starting with lc-
      '[id*="chat-widget"]', // Any id containing chat-widget
      '[class*="chat-widget"]' // Any class containing chat-widget
    ];
    
    const selectorString = widgetSelectors.join(', ');
    
    // Helper to forcefully hide widget elements
    const hideWidgetElements = () => {
      const widgetElements = document.querySelectorAll(selectorString);
      widgetElements.forEach(el => {
        const htmlEl = el as HTMLElement;
        htmlEl.style.setProperty('display', 'none', 'important');
        htmlEl.style.setProperty('visibility', 'hidden', 'important');
        htmlEl.style.setProperty('pointer-events', 'none', 'important');
        htmlEl.style.setProperty('opacity', '0', 'important');
        htmlEl.style.setProperty('width', '0', 'important');
        htmlEl.style.setProperty('height', '0', 'important');
        htmlEl.style.setProperty('overflow', 'hidden', 'important');
        htmlEl.style.setProperty('position', 'absolute', 'important');
        htmlEl.style.setProperty('left', '-9999px', 'important');
      });
      return widgetElements.length;
    };
    
    let mutationObserver: MutationObserver | null = null;
    
    // If user is authenticated, set up persistent hiding
    if (isAuthenticated) {
      // Initial hide
      const hiddenCount = hideWidgetElements();
      if (hiddenCount > 0) {
        logger.debug(`[Auth] Hidden ${hiddenCount} WhatsApp widget elements for authenticated user`);
      }
      
      // PHASE 4: MutationObserver to catch dynamically injected elements
      mutationObserver = new MutationObserver((mutations) => {
        let shouldCheck = false;
        
        for (const mutation of mutations) {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            for (const node of Array.from(mutation.addedNodes)) {
              if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                // Check if the added element or any of its children match widget selectors
                if (
                  el.matches?.(selectorString) ||
                  el.querySelector?.(selectorString) ||
                  el.tagName?.toLowerCase() === 'chat-widget' ||
                  el.className?.includes?.('lc-') ||
                  el.id?.includes?.('chat-widget')
                ) {
                  shouldCheck = true;
                  break;
                }
              }
            }
          }
          if (shouldCheck) break;
        }
        
        if (shouldCheck) {
          const count = hideWidgetElements();
          if (count > 0) {
            logger.debug(`[Auth] MutationObserver: Hidden ${count} dynamically injected widget elements`);
          }
        }
      });
      
      // Observe the entire document for changes
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      // Also observe head for script injections
      mutationObserver.observe(document.head, {
        childList: true,
        subtree: true
      });
      
      logger.debug('[Auth] MutationObserver active for WhatsApp widget hiding');
    }
    
    // Cleanup function
    return () => {
      if (mutationObserver) {
        mutationObserver.disconnect();
        logger.debug('[Auth] MutationObserver disconnected');
      }
    };
  }, [user]);

  // Safety timeout: force auth ready after 12 seconds to prevent infinite spinner
  // when DB is slow or RPCs time out
  const [forceReady, setForceReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      setForceReady(true);
      if (loading || adminLoading || !profileLoaded) {
        logger.warn('[Auth] Force-ready triggered after 12s timeout. loading:', loading, 'adminLoading:', adminLoading, 'profileLoaded:', profileLoaded);
        // Force loading states to resolve
        if (adminLoading) setAdminLoading(false);
        if (loading) setLoading(false);
        if (!profileLoaded) setProfileLoaded(true);
      }
    }, 12000);
    return () => clearTimeout(timer);
  }, []); // Run once on mount

  // Auth is ready only when: not loading, admin check complete, AND profile is loaded
  const isAuthReady = forceReady || (!loading && !adminLoading && profileLoaded);

  return (
    <AuthContext.Provider value={{ user, session, userProfile, loading, isAdmin, adminLoading, primaryRole, isAuthReady, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
